/**
 * Compact setup card for an unconfigured relay on the site dashboard.
 *
 * Four of these share the footprint of one full relay card. Each keeps the relay
 * identity, the setup action that turns the relay into a full operational card,
 * and the power command — an unwired relay can still be switched, which is how a
 * tech proves it works before configuring a load.
 *
 * Contains:
 * - CompactRelayCard — dashboard-only unconfigured relay card
 */
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import { Box, Button, Card, Tooltip, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import RelayCommandsPopup from '@sophocles/views/sites/components/RelayBoxes/RelayCommandsPopup';

export default function CompactRelayCard({
  relay,
  siteId = null,
  canSetUp = false,
  canSendCommands = false,
  onSetup,
  fill = false,
}) {
  const { t } = useTranslation();
  const secondaryRelayNumber = relay.secondary_relay?.relay_number ?? null;
  const relayLabel = `R${relay.relay_number}${secondaryRelayNumber != null ? `+${secondaryRelayNumber}` : ''}`;
  const unusedLabel = t('sites.unusedRelay', 'Unused Relay');
  const setupLabel = t('actions.setup', 'Setup');

  return (
    <Card
      sx={{
        width: '100%',
        // Filling means the grid row owns the height — a half-row inside the 2x2
        // pack. Standing free (the phone carousel), the card keeps its own
        // proportions instead.
        height: fill ? '100%' : 'auto',
        aspectRatio: fill ? 'auto' : '4 / 3',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        p: 'var(--space-3)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
        {canSendCommands && siteId != null ? (
          <Tooltip title={t('sites.relayPowerCommands', 'Power on/off')}>
            <span>
              <RelayCommandsPopup key="relay_commands_popup" siteId={siteId} relay={relay} stateColor={grey[500]} />
            </span>
          </Tooltip>
        ) : (
          <Tooltip title={unusedLabel}>
            <Box
              sx={{
                width: 'calc(var(--space-6) + var(--space-1))',
                height: 'calc(var(--space-6) + var(--space-1))',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 'var(--card-icon-radius)',
                bgcolor: 'var(--status-neutral-tint)',
                color: 'text.secondary',
              }}
            >
              <PowerSettingsNewOutlinedIcon fontSize="small" />
            </Box>
          </Tooltip>
        )}
        <Typography variant="subtitle2" sx={{ minWidth: 0, fontWeight: 'var(--weight-bold)' }} noWrap>
          {relayLabel}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 'var(--space-2)',
        }}
      >
        {canSetUp ? (
          <Button variant="contained" size="small" onClick={onSetup} aria-label={`${setupLabel} ${relayLabel}`}>
            {setupLabel}
          </Button>
        ) : null}
      </Box>
    </Card>
  );
}

CompactRelayCard.propTypes = {
  relay: PropTypes.object.isRequired,
  siteId: PropTypes.number,
  canSetUp: PropTypes.bool,
  canSendCommands: PropTypes.bool,
  onSetup: PropTypes.func.isRequired,
  /** Stretch to the grid row rather than holding the card's own aspect ratio. */
  fill: PropTypes.bool,
};
